import { useTranslation } from "react-i18next";
import { PRODUCT_STATUSES, type ProductStatus } from "@nh/shared";
import { Badge } from "@/shared/ui/core";

export interface StatusBadgeProps {
  status: ProductStatus;
}

/**
 * El estado de una entidad del catálogo, con el mismo color en todas
 * partes.
 *
 * ── Por qué está aquí y no en `products/` ────────────────────────────
 * `productStatusSchema` no es del producto: el paquete lo reutiliza tal
 * cual (`packageSchema.status`), y el día que el catálogo tenga una
 * tercera entidad la reutilizará también. Un borrador es un borrador y
 * tiene que verse igual en las tres listas; con una copia por pantalla,
 * cambiar el color de "archivado" es acordarse de tres sitios.
 *
 * `features/admin/products/components/ProductStatusBadge.tsx` es la
 * versión anterior, idéntica en comportamiento, y sigue en pie porque
 * esa carpeta es de otro agente. Es redundante a partir de ahora: quien
 * la mantenga puede borrarla y apuntar aquí sin cambiar nada más.
 *
 * Tipado como `Record<ProductStatus, …>` a propósito — si el contrato
 * añade un estado, esto deja de compilar en vez de pintar una insignia
 * gris sin etiqueta.
 */
const variants: Record<ProductStatus, "success" | "neutral" | "warning"> = {
  published: "success",
  draft: "neutral",
  archived: "warning",
};

const labels: Record<ProductStatus, string> = {
  published: "Publicado",
  draft: "Borrador",
  archived: "Archivado",
};

export function StatusBadge({ status }: StatusBadgeProps) {
  const { t } = useTranslation();
  return (
    <Badge variant={variants[status]} size="sm" dot>
      {t(labels[status])}
    </Badge>
  );
}

/**
 * Las opciones del filtro y del formulario, traducidas.
 *
 * El ORDEN lo pone `PRODUCT_STATUSES` (borrador → publicado → archivado),
 * que es el ciclo de vida real, y no el orden en que se escribieron las
 * etiquetas de arriba.
 */
export function useStatusOptions(): { value: ProductStatus; label: string }[] {
  const { t } = useTranslation();
  return PRODUCT_STATUSES.map((status) => ({
    value: status,
    label: t(labels[status]),
  }));
}
